import axios from 'axios';

/** What the edit page remembers about the route as it was loaded, beyond what the form shows. */
export interface LoadedPricingRoute {
  /** The route version the page was loaded at. Absent until price-svc reports it. */
  version?: number;
  walletIds: string[];
  isDisabled: boolean;
}

/** A route as the pricing-context-routes GET returns it (the fields the save guards need). */
export interface PricingRouteRecord {
  version?: number;
  walletIds?: string[];
  /** older single-wallet field; price-svc still sets it for single-wallet routes */
  walletId?: string;
  isDisabled?: boolean;
}

/** The wallets a route matches, reading through the deprecated single-wallet field. */
export function routeWalletIds(route: PricingRouteRecord): string[] {
  if (route.walletIds && route.walletIds.length > 0) {
    return [...route.walletIds];
  }

  return route.walletId ? [route.walletId] : [];
}

export function loadedPricingRoute(route: PricingRouteRecord): LoadedPricingRoute {
  return {
    version: route.version,
    walletIds: routeWalletIds(route),
    isDisabled: route.isDisabled ?? false,
  };
}

/**
 * The walletIds to send on update:
 * - undefined: the selection did not change, leave the stored wallets alone
 * - null: every wallet was removed, the route matches any wallet (price-svc rejects an empty list)
 * - a list: replace the stored wallets with it
 */
export function walletIdsUpdate(loaded: string[], selected: string[]): string[] | null | undefined {
  const unchanged = loaded.length === selected.length && loaded.every((id) => selected.includes(id));

  if (unchanged) {
    return undefined;
  }

  return selected.length > 0 ? [...selected] : null;
}

/**
 * Only toggle when the checkbox changed. The toggle is its own write: on a route that was never
 * toggled it records an Enabled event and moves the version, so sending it on every save made any
 * other open editor's save look stale.
 */
export function isDisabledChanged(loaded: LoadedPricingRoute, isDisabled: boolean): boolean {
  return loaded.isDisabled !== isDisabled;
}

/**
 * Adds the version guard to the edit page's PUT body (CIB-4112): the version the form was loaded
 * from, so price-svc rejects the save with 409 if the route changed since (another tab, another
 * user, or a stale read). Without it the page's full metadata list silently overwrites the newer
 * one. Omitted when the route was loaded without a version, which keeps today's behaviour.
 */
export function withRouteSaveGuards<T extends object>(
  payload: T,
  loaded: LoadedPricingRoute | null | undefined
): T & { expectedVersion?: number } {
  const guarded: T & { expectedVersion?: number } = { ...payload };

  if (typeof loaded?.version === 'number') {
    guarded.expectedVersion = loaded.version;
  }

  return guarded;
}

/** The reason a request failed: what api-svc forwarded from price-svc when there is one. */
export function pricingRouteErrorReason(e: unknown): string {
  if (axios.isAxiosError(e) && e.response) {
    const data = e.response.data as unknown;

    if (typeof data === 'string' && data.length > 0) {
      return data;
    }

    const message = (data as { message?: unknown } | null | undefined)?.message;
    if (typeof message === 'string' && message.length > 0) {
      return message;
    }
  }

  return e instanceof Error ? e.message : 'Unknown error';
}

/** The snackbar text for a failed save. */
export function describePricingRouteSaveError(e: unknown): string {
  if (axios.isAxiosError(e) && e.response) {
    if (e.response.status === 409) {
      return 'This pricing route was changed after you opened it, so your save was not applied. Reload the page to see the latest version, then make your change again.';
    }

    if (e.response.status === 404) {
      return 'This pricing route no longer exists, so your save was not applied. Go back to the pricing routes list to see the current routes.';
    }
  }

  return `Problem saving pricing route: ${pricingRouteErrorReason(e)}`;
}
